import React, { useEffect, useRef, useState } from "react";

/**
 * Generic click-out multi-select dropdown. Reused by the assessment
 * templates page (status filter) and any future filter surface. Options
 * are `{ value, label }`; `selected` is the array of currently-picked
 * values, `onChange` receives the next array.
 */
const MultiPickDropdown = ({
  options,
  selected,
  onChange,
  placeholder,
  width = 220,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (v) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };

  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
      ? options.find((o) => o.value === selected[0])?.label || selected[0]
      : `${selected.length} selected`;

  return (
    <div ref={ref} style={{ position: "relative", width }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="kpi-input"
        style={{
          textAlign: "left",
          cursor: "pointer",
          fontSize: 13,
          color: selected.length ? "var(--ink-2)" : "var(--ink-4)",
          height: 36,
          paddingTop: 0,
          paddingBottom: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {label}
        </span>
        <span style={{ fontSize: 10, color: "var(--ink-4)", marginLeft: 8 }}>
          ▾
        </span>
      </button>
      {open ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            background: "var(--surface)",
            border: "1px solid var(--rule)",
            borderRadius: 10,
            boxShadow:
              "0 4px 6px -1px rgba(0,0,0,0.07), 0 16px 32px -4px rgba(0,0,0,0.10)",
            zIndex: 700,
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          {options.length === 0 ? (
            <div
              style={{
                padding: "10px 12px",
                color: "var(--ink-4)",
                fontSize: 12,
                fontStyle: "italic",
              }}
            >
              No options
            </div>
          ) : (
            options.map((opt) => {
              const isOn = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  htmlFor={`status-${opt.value}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 12px",
                    cursor: "pointer",
                    background: isOn ? "#f0f9ff" : "transparent",
                    borderBottom: "1px solid var(--rule-2)",
                    fontSize: 13,
                    color: "var(--ink-2)",
                    fontWeight: isOn ? 600 : 400,
                    margin: 0,
                  }}
                >
                  <input
                    type="checkbox"
                    className="kpi-checkbox"
                    id={`status-${opt.value}`}
                    checked={isOn}
                    onChange={() => toggle(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
};

export default MultiPickDropdown;
